import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  buildLearnerNavigationState,
  getLearnerNavigationState,
  navigateLearnerBack,
} from "src/pages/coaching/utils/manageNavigation";
import { CourseSummaryComponent, LoadingCoursePage } from "../../../components/course/view"
import { useSelector } from "react-redux";
import { LearningRecordCourse, UpdateCourseContentSessionRequest } from "src/types/coaching/learners";
import React, { useEffect, useMemo, useState } from "react";
import { CourseContext, CourseContextProps } from "../../../components/course/view/context";

import _ from 'lodash';

import { useDispatch } from "react-redux";
import {
  completeCourseContentSession,
  createCourseContentSession,
  createLessonAnalyticsEvent,
  fetchAssessmentReport,
  fetchCourseContentSession,
  fetchLearningRecordCourse,
  fetchLessonForLearningRecord,
  fetchLessonPreviewForLearningRecord,
  invalidateCourseContentSession,
  invalidateRetakeCourse,
  invalidateStartCourse,
  recordAnswerForQuestion,
  recordLessonAnalyticsEvent,
  recordQuestionSession,
  retakeCourse,
  startCourse,
  updateCourseContentSession
} from "src/redux/actions/coaching/learners";

import { CourseStructureComponent } from "../../../components/course/view/structure";
import { Assessment } from "src/types/coaching/courses";

import userRoleService from "src/utils/userRoles.service";
import PermissionDenined from "src/components/permissionDenied";
import { CourseNotFound } from "src/pages/coaching/components/notFound";

import styles from "./view.module.scss";
import CourseReport from "src/pages/coaching/components/course/view/reports";

export const ViewCoursePage: React.FC<object> = () => {
  const dispatch = useDispatch();
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const learnerNavigation = useMemo(
    () => getLearnerNavigationState(location.state),
    [location.state],
  );

  const courseId = params.courseId;

  const course = useSelector((state: any) =>
    _.get(state.learners, `course[${courseId}].item`) as LearningRecordCourse,
  );

  const loading = useSelector((state: any) =>
    _.get(state.learners, `course[${courseId}].loading`),
  );

  const courseErrorCode = useSelector((state: any) => {
    const error = _.get(state.learners, `course[${courseId}].error`);
    return _.get(error, "errors[0].code", _.get(error, "code"));
  });

  const startingCourse = useSelector((state: any) => {
    const starting = _.get(state.learners, `course[${courseId}].start.starting`);
    return starting;
  });

  const courseStarted = useSelector((state: any) => {
    return _.get(state.learners, `course[${courseId}].start.started`);
  });

  const retakingCourse = useSelector((state: any) => {
    return _.get(state.learners, `course[${courseId}].retake.retaking`);
  });

  const courseRetaken = useSelector((state: any) => {
    return _.get(state.learners, `course[${courseId}].retake.retaken`);
  });

  const courseRetakenId = useSelector((state: any) => {
    return _.get(state.learners, `course[${courseId}].retake.id`);
  });

  const [courseContext, setCourseContext] = React.useState<CourseContextProps>({
    id: courseId,
    startAssessment: () => { },
    endAssessment: () => { },
  });

  useEffect(() => {
    if (courseId) {
      dispatch(fetchLearningRecordCourse(courseId));
    }

    setCourseContext((prev) => {
      return {
        ...prev,
        id: courseId,
        startAssessment: () => { },
        endAssessment: () => { },
      }
    });
  }, []);

  useEffect(() => {
    setCourseContext((prev) => {
      return {
        ...prev,
        getContentSessions: handleGetContentSessions,
        onBack: handleBackClicked,
        onContentClicked: handleContentClicked,
        startCourse: handleStartCourse,
        endCourse: handleEndCourse,
        retakeCourse: handleRetakeCourse
      }
    });
  }, [course]);

  useEffect(() => {
    if (courseStarted) {
      navigate(`/coaching/courses/${courseId}/contents?index=0`, {
        state: buildLearnerNavigationState(learnerNavigation),
      });
      dispatch(invalidateStartCourse(course.id));
    }
  }, [courseStarted, courseId, course?.id, dispatch, learnerNavigation, navigate]);

  useEffect(() => {
    setCourseContext((prev) => {
      return {
        ...prev,
        startingCourse: startingCourse
      }
    });
  }, [startingCourse]);
  
  useEffect(() => {
    setCourseContext((prev) => {
      return {
        ...prev,
        retakingCourse: retakingCourse,
      }
    });
  }, [retakingCourse]);

  useEffect(() => {
    if (courseRetaken && courseRetakenId) {
      navigate(`/coaching/courses/${courseRetakenId}/contents?index=0`, {
        state: buildLearnerNavigationState(learnerNavigation),
      });
      dispatch(invalidateRetakeCourse(course.id));
    }
  }, [courseRetaken, courseRetakenId, course?.id, dispatch, learnerNavigation, navigate]);

  const handleBackClicked = () => {
    navigateLearnerBack(navigate, learnerNavigation);
  };

  const handleContentClicked = (index) => {
    if (course.status !== "NOT_STARTED") {
      navigate(`/coaching/courses/${courseId}/contents?index=${index}`, {
        state: buildLearnerNavigationState(learnerNavigation),
      });
    }
  };

  const handleStartCourse = () => {
    dispatch(startCourse(course.id));
  }

  const handleEndCourse = () => {
    // dispatch(endCourse(course.id));
  }

  const handleRetakeCourse = () => {
    dispatch(retakeCourse(course.id));
  }

  const handleGetContentSessions = () => {
    const sessions = _.get(course, 'sessions') || [];
    return sessions;
  }

  if (!userRoleService.canAccessCoaching()) {
    return <PermissionDenined className={styles.permissionDenied} />;
  }

  if (courseErrorCode === 10002) {
    return <CourseNotFound
      message="This course has probably been deleted."
      secondaryMessage="Start exploring and try taking another course now!"
      onBack={() => {
      navigateLearnerBack(navigate, learnerNavigation);
    }} />
  }

  if (courseErrorCode === 10007 || Number(courseErrorCode) === 99999) {
    return <CourseNotFound onBack={() => {
      navigateLearnerBack(navigate, learnerNavigation);
    }} />
  }

  if ((loading && course?.id != courseId) || !course) {
    return (
      <div className={styles.courseContainer}>
        <div className={styles.left}>
          <div className={styles.courseDetails}>
            <div
              className={styles.back}
              onClick={() => {
                navigateLearnerBack(navigate, learnerNavigation);
              }}
            >
              <i className="paperflite-left-arrow"></i>
              <span>my courses</span>
            </div>

            <LoadingCoursePage />
          </div>
        </div>
      </div>
    );
  }


  return (
    <CourseContext.Provider value={courseContext}>
      <CourseSummaryComponent
        course={course}
      />
    </CourseContext.Provider>
  );
}

export const ViewCourseStructurePage: React.FC<object> = () => {
  const dispatch = useDispatch();
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const backPath = location.state?.from as string | undefined;
  const requestedAssessmentReportRef = React.useRef<{ id: string; session: string } | null>(null);
  const [searchParams] = useSearchParams();

  const courseId = params.id;

  const course = useSelector((state: any) =>
    _.get(state.learners, `course[${courseId}].item`) as LearningRecordCourse,
  );

  const contentIndex = useMemo(() => {
    return parseInt(searchParams.get("index") || "0");
  }, [searchParams]);

  const childContentIndex = useMemo(() => {
    return parseInt(searchParams.get("child") || "0");
  }, [searchParams]);

  const courseContent = useMemo(() => {
    const content = _.get(course, `course.contents[${contentIndex}]`);
    if (childContentIndex >= 0 && content?.entityType === 'section') {
      return _.get(content, `contents[${childContentIndex}]`);
    }
    return content;
  }, [course, contentIndex, childContentIndex]);

  const loading = useSelector((state: any) =>
    _.get(state.learners, `course[${courseId}].loading`),
  );

  const courseErrorCode = useSelector((state: any) => {
    const error = _.get(state.learners, `course[${courseId}].error`);
    return _.get(error, "errors[0].code", _.get(error, "code"));
  });

  const learnerNavigation = useMemo(
    () => getLearnerNavigationState(location.state),
    [location.state],
  );

  useEffect(() => {
    const contentId = searchParams.get("contentId");
    if (!contentId || !course?.course?.contents) {
      return;
    }

    const contents = course.course.contents;
    const navigationState = buildLearnerNavigationState(learnerNavigation);

    for (let i = 0; i < contents.length; i++) {
      const item = contents[i];

      if (item.id === contentId) {
        navigate(`/coaching/courses/${courseId}/contents?index=${i}`, {
          replace: true,
          ...(navigationState ? { state: navigationState } : {}),
        });
        return;
      }

      if (item.entityType === "section" && item.contents?.length) {
        const childIndex = item.contents.findIndex((c) => c.id === contentId);
        if (childIndex >= 0) {
          navigate(
            `/coaching/courses/${courseId}/contents?index=${i}&child=${childIndex}`,
            {
              replace: true,
              ...(navigationState ? { state: navigationState } : {}),
            },
          );
          return;
        }
      }
    }
  }, [course, courseId, learnerNavigation, navigate, searchParams]);

  const courseContentSession = useSelector((state: any) => {
    const sessions = _.get(state.learners, `course[${course?.id}].item.sessions`) || [];
    const item = sessions?.find((item) => item.id === courseContent?.id && item.type === courseContent?.entityType);

    return item;
  }, _.isEqual);

  const { created, fetched, fetchedReport, session } = useMemo(() => {
    const created = _.get(courseContentSession, "created") || false;
    const fetched = _.get(courseContentSession, "fetched") || false;
    const fetchedReport = _.get(courseContentSession, "fetchedReport") || false;

    const session = _.get(courseContentSession, "session");

    return {
      created,
      fetched,
      fetchedReport,
      session,
    }
  }, [courseContentSession]);

  const contents = useMemo(() => course?.course?.contents || [], [course]);

  const asset = useSelector((state: any) => {
    if (courseContent?.entityType !== "lesson") {
      return undefined;
    }

    const assetLoading = _.get(state, `learners.course[${courseId}].lessons['${courseContent.id}'].loading`, true);
    const item = _.get(state, `learners.course[${courseId}].lessons['${courseContent.id}'].item`);

    const previewItem = _.get(state, `learners.course[${courseId}].lessons['${courseContent.id}'].preview.item`);

    return {
      loading: assetLoading,
      item: item,
      preview: previewItem,
    };
  }, _.isEqual);

  const canComplete = useSelector((state: any) => {
    const courseItem = _.get(state.learners, `course[${course?.id}].item`);
    const sessions = _.get(courseItem, 'sessions') || [];
    
    const checkComplete = (contentList) => {
      return contentList.every((content) => {
        if (content.entityType === 'section' && _.size(content.contents) > 0) {
          return checkComplete(content.contents);
        }

        const session = sessions.find((session) => session.id === content.id);
        
        if (content.entityType === 'podcast') {
          return session?.status === 'COMPLETED';
        } else if (content.entityType === 'simulation') {
          return session?.status === 'COMPLETED' && session?.score > 0;
        } else if(content.entityType === 'assessment'){
          return session?.status === 'COMPLETED' ;
        }

        return session != null;
      });
    };

    return checkComplete(contents);
  }, _.isEqual);

  const completingCourse = useSelector((state: any) => {
    const completing = _.get(state.learners, `course[${courseId}].complete.completing`);
    return completing;
  });

  const courseCompleted = useSelector((state: any) => {
    const completed = _.get(state.learners, `course[${courseId}].complete.completed`);
    return completed;
  });

  const [courseContext, setCourseContext] = React.useState<CourseContextProps>({
    id: courseId,
    startAssessment: () => { },
    endAssessment: () => { },
  });

  useEffect(() => {
    if (courseId && !course) {
      dispatch(fetchLearningRecordCourse(courseId));
    }

    setCourseContext((prev) => {
      return {
        ...prev,
        id: courseId,
        startAssessment: handleStartAssessment,
        endAssessment: handleEndAssessment,
      }
    });
  }, []);

  useEffect(() => {
    setCourseContext((prev: any) => {
      return {
        ...prev,
        getContentSessions: handleGetContentSessions,
        onBack: handleBackClicked,
        onContentClicked: handleContentClicked,
        startAssessment: handleStartAssessment,
        endAssessment: handleEndAssessment,
      }
    });
  }, [course]);

  useEffect(() => {
    setCourseContext((prev) => {
      return {
        ...prev,
        courseContent: courseContent,
        createCourseContentSession: handleCreateCourseContentSession,
        fetchCourseContentSession: handleFetchCourseContentSession,
        updateCourseContentSession: handleUpdateCourseContentSession,
        completeCourseContentSession: handleCompleteCourseContentSession,
        recordAnswerForQuestion: handleRecordAnswerForQuestion,
        recordQuestionSession: handleRecordQuestionSession,
        fetchAssessmentReport: handleFetchAssessmentReport,
        createLessonAnalyticsEvent: handleCreateLessonAnalyticsEvent,
        recordLessonAnalyticsEvent: handleRecordLessonAnalyticsEvent,
        endAssessment: handleEndAssessment,
        fetchLesson: handleFetchLesson,
        fetchLessonPreview: handleFetchLessonPreview,
      }
    });
  }, [courseContent, session]);

  useEffect(() => {
    setCourseContext((prev) => {
      return {
        ...prev,
        courseContentSession: courseContentSession
      }
    });
  }, [courseContentSession]);

  useEffect(() => {
    setCourseContext((prev) => {
      return {
        ...prev,
        asset: asset
      }
    });
  }, [asset]);

  //Assessment related side effects
  useEffect(() => {
    if (created && session && courseContent?.entityType === "assessment") {
      handleStartAssessment(courseContent as Assessment);
      dispatch(invalidateCourseContentSession({
        course: course.course!.id!,
        learningRecord: course.id!,
        id: courseContentSession.id,
        type: "assessment",
      }));
    }
  }, [created, session]);

  useEffect(() => {
    if (fetched && session && courseContent?.entityType === "assessment") {
      handleStartAssessment(courseContent as Assessment);
      dispatch(invalidateCourseContentSession({
        course: course.course!.id!,
        learningRecord: course.id!,
        id: courseContentSession.id,
        type: "assessment",
      }));
    }
  }, [fetched, session]);

  useEffect(() => {
    const requestedReport = requestedAssessmentReportRef.current;
    const isRequestedReport =
      requestedReport?.id === courseContent?.id &&
      requestedReport?.session === session;

    if (fetchedReport && session && courseContent?.entityType === "assessment" && isRequestedReport) {
      requestedAssessmentReportRef.current = null;
      handleStartAssessment(courseContent as Assessment);
      dispatch(invalidateCourseContentSession({
        course: course.course!.id!,
        learningRecord: course.id!,
        id: courseContentSession.id,
        type: "assessment",
      }));
    }
  }, [fetchedReport, session]);

  useEffect(() => {
    setCourseContext((prev) => {
      return {
        ...prev,
        canCompleteCourse: canComplete,
        completingCourse: completingCourse,
        completedCourse: courseCompleted,
      }
    });
  }, [completingCourse, courseCompleted, canComplete]);

  const handleBackClicked = () => {
    navigate(`/coaching/courses/${course.id}`, {
      state: buildLearnerNavigationState(learnerNavigation),
    });
  };

  const handleGetContentSessions = () => {
    const sessions = _.get(course, 'sessions') || [];
    return sessions;
  }

  const handleContentClicked = (index: number, section?: number | null) => {
    if (course.status !== "NOT_STARTED") {
      const navigationState = buildLearnerNavigationState(learnerNavigation);
      if (section != null && section !== -1) {
        navigate(
          `/coaching/courses/${courseId}/contents?index=${index}&child=${section}`,
          navigationState ? { state: navigationState } : undefined,
        );
      } else {
        navigate(
          `/coaching/courses/${courseId}/contents?index=${index}`,
          navigationState ? { state: navigationState } : undefined,
        );
      }
    }
  };

  const handleCreateCourseContentSession = (courseContent) => {
    dispatch(createCourseContentSession({
      course: course.course!.id!,
      learningRecord: course.id!,
      id: courseContent.id,
      type: courseContent.entityType,
    }));
  }

  const handleFetchCourseContentSession = (courseContent, session) => {
    dispatch(fetchCourseContentSession({
      course: course.course!.id!,
      learningRecord: course.id!,
      id: courseContent.id,
      type: courseContent.entityType,
      session: session,
    }));
  }

  const handleUpdateCourseContentSession = (courseContent, session, duration, progress) => {
    const request = {
      course: course.course.id!,
      learningRecord: course.id!,
      id: courseContent.id,
      type: courseContent.entityType,
      session: session,
      duration: duration,
      percentCompleted: progress
    } as UpdateCourseContentSessionRequest;

    dispatch(updateCourseContentSession(request));
  }

  const handleCompleteCourseContentSession = (courseContent, session, duration, progress) => {
    const request = {
      course: course.course.id!,
      learningRecord: course.id!,
      id: courseContent.id,
      type: courseContent.entityType,
      session: session,
      duration: duration,
      percentCompleted: progress
    };

    dispatch(completeCourseContentSession(request));
  }

  const handleStartAssessment = (assessment) => {
    setCourseContext((prev) => {
      return {
        ...prev,
        activeAssessment: assessment,
      }
    });
  }

  const handleEndAssessment = (assessment) => {
    setCourseContext((prev) => {
      return {
        ...prev,
        activeAssessment: null,
      }
    });

    dispatch(invalidateCourseContentSession({
      course: course.course!.id!,
      learningRecord: course.id!,
      id: assessment!.id,
      type: "assessment",
    }));
  }

  const handleRecordAnswerForQuestion = (assessment, question, answer) => {
    dispatch(recordAnswerForQuestion({
      course: course.course.id!,
      learningRecord: course.id!,
      assessment: assessment.id,
      question: question,
      answer: answer,
    }));
  }

  const handleRecordQuestionSession = (assessment, question, answer, duration) => {
    const request = {
      course: course.course.id!,
      learningRecord: course.id!,
      assessment: assessment.id,
      session: session,
      question: question,
      answer: answer,
      duration: duration,
    };

    dispatch(recordQuestionSession(request));
  }

  const handleFetchAssessmentReport = (courseContent, session) => {
    requestedAssessmentReportRef.current = {
      id: courseContent.id,
      session,
    };

    dispatch(fetchAssessmentReport({
      course: course.course!.id!,
      learningRecord: course.id!,
      id: courseContent.id,
      type: courseContent.entityType,
      session: session,
    }));
  }

  //Lesson
  const handleCreateLessonAnalyticsEvent = (lesson) => {
    dispatch(createLessonAnalyticsEvent({
      course: course!.course!.id!,
      learningRecord: course!.id!,
      lesson: lesson!.id,
      type: 100,
      eventDate: new Date().getTime(),
    }));
  }

  const handleRecordLessonAnalyticsEvent = (lesson, session, request) => {
    dispatch(recordLessonAnalyticsEvent({
      course: course!.course!.id!,
      learningRecord: course!.id!,
      lesson: lesson!.id,
      session: session,
      ...request,
    }));
  }

  const handleFetchLesson = (lesson) => {
    dispatch(fetchLessonForLearningRecord({
      learningRecord: course.id!,
      lesson: lesson.id,
    }));
  }

  const handleFetchLessonPreview = (lesson) => {
    dispatch(fetchLessonPreviewForLearningRecord({
      learningRecord: course.id!,
      lesson: lesson.id,
      includeText: true,
    }));
  }

  if (!userRoleService.canAccessCoaching()) {
    return <PermissionDenined className={styles.permissionDenied} />;
  }

  if (courseErrorCode === 10002) {
    return <CourseNotFound
      message="This course has probably been deleted."
      secondaryMessage="Start exploring and try taking another course now!"
      onBack={() => {
      navigateLearnerBack(navigate, learnerNavigation);
    }} />
  }

  if (courseErrorCode === 10007 || Number(courseErrorCode) === 99999) {
    return <CourseNotFound onBack={() => {
      navigateLearnerBack(navigate, learnerNavigation);
    }} />
  }

  if ((loading && course?.id != courseId) || !course) {
    return <div className={styles.loading}>Loading course...</div>
  }

  return (
    <CourseContext.Provider value={courseContext}>
      <CourseStructureComponent course={course} />
    </CourseContext.Provider>
  );
}

export const ViewCourseReportsPage: React.FC<object> = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const courseId = params.learningId;
  const learnerNavigation = useMemo(
    () => getLearnerNavigationState(location.state),
    [location.state],
  );
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [secondaryView, setSecondaryView] = useState<null | "call-recording" | "call-recording-minimized">(null);

  const {loading, course, courseErrorCode} = useSelector((state: any) => {
    const error = _.get(state.learners, `course[${courseId}].error`);
    return {
      loading: _.get(state.learners, `course[${courseId}].loading`),
      course: _.get(state.learners, `course[${courseId}].item`) as LearningRecordCourse,
      courseErrorCode: _.get(error, "errors[0].code", _.get(error, "code")),
    };
  });

  useEffect(() => {
    if (courseId && !course) {
      dispatch(fetchLearningRecordCourse(courseId));
    }
  }, []);

  const [courseContext, setCourseContext] = useState<CourseContextProps>({
    id: courseId,
  });

  useEffect(() => {
    setCourseContext({
      id: courseId,
      isSidebarCollapsed,
      setIsSidebarCollapsed,
      activeTab,
      setActiveTab,
      secondaryView,
      changeSecondaryView: setSecondaryView,
      onBack: () => navigateLearnerBack(navigate, learnerNavigation),
    });
  }, [courseId, isSidebarCollapsed, activeTab, secondaryView, learnerNavigation, navigate]);

  if (!userRoleService.canAccessCoaching()) {
    return <PermissionDenined className={styles.permissionDenied} />;
  }

  if (courseErrorCode === 10002) {
    return <CourseNotFound
      message="This course has probably been deleted."
      secondaryMessage="Start exploring and try taking another course now!"
      onBack={() => {
      navigateLearnerBack(navigate, learnerNavigation);
    }} />
  }

  if (courseErrorCode === 10007 || Number(courseErrorCode) === 99999) {
    return <CourseNotFound onBack={() => {
      navigateLearnerBack(navigate, learnerNavigation);
    }} />
  }

  if ((loading && course?.id != courseId) || !course) {
    return <div className={styles.loading}>Loading course...</div>;
  }

  return (
    <CourseContext.Provider value={courseContext}>
      <CourseReport course={course} />
    </CourseContext.Provider>
  );
};
